import React, { useState, useEffect, useRef } from "react";
import { Container, Form, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import "./Login.css";
import illustration from "../../../assets/ilustracao.png";
import logo from "../../../assets/Logo.png";
import { login } from "../../../api/auth/login";
import { loginErrorMessage } from "./loginError";

interface LoginProps {
  onNavigate?: () => void;
}

const LoginPage: React.FC<LoginProps> = ({ onNavigate }) => {
  const navigate = useNavigate();
  const { setAuthUser, isAuthenticated } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState({ email: "", password: "" });
  const errorRef = useRef<HTMLDivElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const submitting = useRef(false);
  useEffect(() => { if (errorMessage) errorRef.current?.focus(); }, [errorMessage]);
  // Se já está autenticado, redirecionar para a rota correta
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/searchscreen");
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting.current) return;
    setErrorMessage("");
    const cleanEmail = email.trim();
    const errors = {
      email: !cleanEmail ? "Informe seu e-mail." : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail) ? "Informe um e-mail válido." : "",
      password: !password ? "Informe sua senha." : "",
    };
    setFieldErrors(errors);
    if (errors.email || errors.password) {
      (errors.email ? emailRef : passwordRef).current?.focus();
      return;
    }
    submitting.current = true;
    setLoading(true);
    try {
        const result = await login(cleanEmail, password);
        if (result.mfaRequired) {
          // MFA ativado — redirecionar para tela de código
          navigate("/verify-mfa", { state: { email: result.email } });
          return;
        }

        setAuthUser(result.session);
        navigate("/searchscreen");
    } catch (error) {
        setErrorMessage(loginErrorMessage(error));
    } finally {
        submitting.current = false;
        setLoading(false);
    }
  };

  return (
    <Container fluid className="login-container">
      <div className="left-section">
        <div className="branding">
          <img
            src={logo}
            alt="Logo"
            className="logo"
            width={55}
            height={55}
            onClick={() => navigate("/home")}
          />
          <span className="brand-name">Insecta</span>
        </div>

        <div className="login-content">
          <h1 className="title">Entrar</h1>
          <h2 className="title">
            Acesse sua conta
            <br />
            para continuar.
          </h2>

          <p className="title">
            Se você não tem uma conta, registre-se.
            <br />
            Você pode se{" "}
            <a href="/register" className="register-link" onClick={onNavigate}>
              registrar aqui!
            </a>
          </p>
        </div>

        <div className="illustration-container">
          <img src={illustration} alt="Ilustração" className="illustration" />
        </div>
      </div>

      <div className="right-section">
        <div className="form-container">
          <p className="form-title">Insira suas informações abaixo</p>
          <Form onSubmit={handleSubmit} className="login-form" noValidate aria-label="Entrar na conta" aria-busy={loading}>
            {errorMessage && <div ref={errorRef} tabIndex={-1} className="login-error-message" role="alert">{errorMessage}</div>}
            <Form.Group className="mb-3" controlId="login-email">
              <Form.Label>E-mail</Form.Label>
              <Form.Control
                ref={emailRef}
                type="email"
                placeholder="Entre com o e-mail"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setFieldErrors(previous => ({ ...previous, email: "" })); }}
                autoComplete="username"
                disabled={loading}
                aria-invalid={!!fieldErrors.email}
                aria-describedby={fieldErrors.email ? "login-email-error" : undefined}
                required
                className="form-input"
              />
              {fieldErrors.email && <div id="login-email-error" className="login-field-error" role="alert">{fieldErrors.email}</div>}
            </Form.Group>

            <Form.Group className="mb-2" controlId="login-password">
              <Form.Label>Senha</Form.Label>
              <Form.Control
                ref={passwordRef}
                type="password"
                placeholder="Senha"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setFieldErrors(previous => ({ ...previous, password: "" })); }}
                autoComplete="current-password"
                disabled={loading}
                aria-invalid={!!fieldErrors.password}
                aria-describedby={fieldErrors.password ? "login-password-error" : undefined}
                required
                className="form-input"
              />
              {fieldErrors.password && <div id="login-password-error" className="login-field-error" role="alert">{fieldErrors.password}</div>}
            </Form.Group>

            <div className="forgot-password-container">
              <a href="/forgot-password" className="forgot-password">
                Esqueceu a senha?
              </a>
            </div>

            <Button type="submit" className="login-button" disabled={loading} aria-label={loading ? "Entrando…" : "Entrar"}>
              {loading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                  style={{ width: "1rem", height: "1rem" }}
                ></span>
              ) : (
                "Entrar"
              )}
            </Button>
          </Form>
        </div>
      </div>
    </Container>
  );
};

export default LoginPage;
